// ============================================
// AdjustStockModal
// Điều chỉnh tồn kho cho MỘT dòng tồn (sheet 2 — mục #23, #29, #31)
// ============================================
//
// Mọi loại điều chỉnh đều đi qua CÙNG một endpoint POST /inventory/adjust/,
// khác nhau ở `type`. Nên gộp làm một modal thay vì ba màn riêng:
//   SET_ABSOLUTE — kiểm đếm: nhập SỐ ĐẾM ĐƯỢC, server tự tính chênh (mục #23)
//   INCREASE     — biết rõ thêm N cái
//   DECREASE     — biết rõ mất N cái (thay cho LOSS cũ)  (mục #31)
//   DAMAGE       — hàng hư hỏng                          (mục #31)
//
// Bộ loại cũ (CORRECTION / LOSS / TRANSFER_IN / TRANSFER_OUT / RECEIPT / SALE)
// KHÔNG tồn tại trong ChoiceField của BE — gửi lên là 400. Thay toàn bộ
// 04/09/2026.
//
// KHÔNG cho chọn tay: `RETURN` và `RESTOCK` (bút toán do luồng trả hàng và
// phiếu nhập sinh ra — chỉnh tay sẽ tạo bút toán trùng), và
// `MANUAL_CORRECTION` (chỉ CỘNG THÊM, không diễn đạt được "đếm thiếu", nên
// `SET_ABSOLUTE` thay thế hoàn toàn cho kiểm đếm).
//
// Chuyển kho giữa hai cửa hàng KHÔNG đi qua đây: dùng phiếu nhập
// (`/stock-imports?mode=transfer`, nút "Transfer Stock" ở trang Tồn kho). Đừng
// ghép tạm bằng một cặp INCREASE/DECREASE — đó là hai bút toán ở hai kho, gộp
// lại thì mất liên kết và không truy được hàng đi đâu khi đối soát.
//
// `reason` BE cho optional, nhưng FE vẫn BẮT BUỘC: không có lý do thì lịch sử
// điều chỉnh vô dụng, không quy được trách nhiệm. Ràng buộc nghiệp vụ của
// mình, chặt hơn hợp đồng — cố ý.
//
// `quantity` gửi lên luôn KHÔNG ÂM; chiều do `type` quyết định. Phần xem trước
// "tồn sau" ở đây chỉ là ước lượng để người dùng yên tâm — con số thật lấy từ
// `resulting_stock` trong bản ghi BE trả về.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { LossReason, StockAdjustmentType, UUID } from '@conai/shared-types';
import { useI18n } from '../../../i18n';
import { useCreateAdjustment } from '../../../hooks';
import type { InventoryItem } from './InventoryTableRow';

/**
 * Nguyên nhân hao hụt (mục #220, BE lên QA đợt deploy kế 09/09/2026).
 *
 * BE bắt buộc field này khi `type` là DECREASE/DAMAGE và 400 nếu gửi kèm ở
 * type khác — nên ô chỉ hiện đúng lúc cần, không phải checkbox "tuỳ chọn"
 * lúc nào cũng có mặt.
 */
const LOSS_REASONS: { value: LossReason; labelKey: string }[] = [
  { value: 'THEFT', labelKey: 'lossReasonTheft' },
  { value: 'EXPIRED', labelKey: 'lossReasonExpired' },
  { value: 'DAMAGED', labelKey: 'lossReasonDamaged' },
  { value: 'OTHER', labelKey: 'lossReasonOther' },
];

/** Key chống trùng — phải là chuỗi KHÁC RỖNG, xem ghi chú ở nơi dùng. */
function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `adjust-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Loại cho phép chọn tay.
 *
 * `direction` mô tả cách diễn giải con số người dùng gõ:
 *   'absolute' — SỐ ĐẾM THỰC TẾ, server tự tính chênh (tăng hay giảm đều được)
 *   'in' / 'out' — số lượng thêm vào / bớt đi
 *
 * KHÔNG có 'both' nữa: `quantity` phải không âm, chiều do `type` quyết định.
 *
 * `MANUAL_CORRECTION` cố ý KHÔNG có ở đây dù hợp lệ — nó chỉ cộng thêm, không
 * diễn đạt được "đếm thiếu", nên với kiểm đếm thì `SET_ABSOLUTE` thay thế
 * hoàn toàn. `RETURN` / `RESTOCK` cũng không: đó là bút toán do luồng trả hàng
 * và phiếu nhập sinh ra, cho chỉnh tay sẽ tạo bút toán trùng.
 */
const MANUAL_TYPES: {
  value: Extract<StockAdjustmentType, 'SET_ABSOLUTE' | 'INCREASE' | 'DECREASE' | 'DAMAGE'>;
  labelKey: string;
  direction: 'absolute' | 'out' | 'in';
}[] = [
  { value: 'SET_ABSOLUTE', labelKey: 'adjustTypeSetAbsolute', direction: 'absolute' },
  { value: 'INCREASE', labelKey: 'adjustTypeIncrease', direction: 'in' },
  { value: 'DECREASE', labelKey: 'adjustTypeDecrease', direction: 'out' },
  { value: 'DAMAGE', labelKey: 'adjustTypeDamage', direction: 'out' },
];

interface AdjustStockModalProps {
  item: InventoryItem | null;
  storeId: UUID | undefined;
  onClose: () => void;
  /** Gọi sau khi điều chỉnh thành công (để trang cha đóng modal chi tiết...). */
  onSuccess?: () => void;
}

export function AdjustStockModal({ item, storeId, onClose, onSuccess }: AdjustStockModalProps) {
  const { t } = useI18n();
  const { mutate: createAdjustment, isPending } = useCreateAdjustment();

  const [type, setType] = useState<(typeof MANUAL_TYPES)[number]['value']>('SET_ABSOLUTE');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [lossReason, setLossReason] = useState<LossReason | ''>('');
  const [error, setError] = useState<string | null>(null);

  /** BE bắt buộc `loss_reason` cho hai loại này, và 400 nếu gửi kèm loại khác. */
  const requiresLossReason = type === 'DECREASE' || type === 'DAMAGE';

  /**
   * Chống trùng cho đường MỘT DÒNG (BE nhận `idempotency_key` trong body).
   *
   * Trước đây đường này không gửi key nào, chống trùng chỉ dựa vào việc nút bị
   * `disabled` lúc đang gửi. Không đủ: request timeout hoặc mất mạng thì nút
   * mở lại trong khi BE có thể đã ghi xong — bấm lại là TRỪ KHO HAI LẦN, và ở
   * màn tồn kho thì đó là mất hàng thật chứ không phải phiền toái giao diện.
   *
   * Dùng `useRef` chứ không `useState`: key phải sống qua mọi lần render và
   * mọi lần bấm lại, chỉ đổi khi chuyển sang dòng khác.
   */
  const idempotencyKeyRef = useRef(newIdempotencyKey());

  // Mở lại cho dòng khác thì reset sạch — tránh mang lý do của lần trước sang.
  useEffect(() => {
    if (item) {
      setType('SET_ABSOLUTE');
      setQuantity('');
      setReason('');
      setLossReason('');
      setError(null);
      // Dòng khác = bút toán khác, phải là key khác. Dùng lại key cũ thì BE
      // coi lần chỉnh này là bản trùng của lần trước và bỏ qua trong im lặng.
      idempotencyKeyRef.current = newIdempotencyKey();
    }
  }, [item]);

  const activeType = MANUAL_TYPES.find((x) => x.value === type) ?? MANUAL_TYPES[0];

  // Đổi sang loại KHÔNG cần nguyên nhân thì xoá lựa chọn cũ — giữ lại là gửi
  // kèm `loss_reason` cho INCREASE/SET_ABSOLUTE, và BE trả 400 vì type đó
  // không nhận field này.
  useEffect(() => {
    if (!requiresLossReason) setLossReason('');
  }, [requiresLossReason]);

  /**
   * Con số GỬI LÊN — luôn không âm, vì chiều do `type` quyết định ở BE.
   *
   * `SET_ABSOLUTE` cho phép 0 (đếm ra không còn cái nào là thông tin hợp lệ);
   * các loại khác thì 0 vô nghĩa nên coi là chưa nhập.
   */
  const payloadQuantity = useMemo(() => {
    const parsed = Number.parseInt(quantity, 10);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    if (activeType.direction !== 'absolute' && parsed === 0) return null;
    return Math.abs(parsed);
  }, [quantity, activeType.direction]);

  /**
   * Tồn sau khi ghi, để xem trước.
   *
   * `SET_ABSOLUTE` thì tồn sau CHÍNH LÀ số vừa nhập — không cộng vào tồn cũ.
   * Đây chỉ là ước lượng hiển thị; con số thật lấy từ `resulting_stock` trong
   * bản ghi BE trả về sau khi ghi xong.
   */
  const projectedQuantity = useMemo(() => {
    if (!item || payloadQuantity === null) return null;
    if (activeType.direction === 'absolute') return payloadQuantity;
    if (activeType.direction === 'out') return item.currentQuantity - payloadQuantity;
    return item.currentQuantity + payloadQuantity;
  }, [item, payloadQuantity, activeType.direction]);

  if (!item) return null;

  const handleSubmit = () => {
    setError(null);

    if (!storeId) {
      setError(t('adjustErrorNoStore'));
      return;
    }
    if (payloadQuantity === null) {
      setError(t('adjustErrorQuantity'));
      return;
    }
    if (!reason.trim()) {
      setError(t('adjustErrorReason'));
      return;
    }
    if (requiresLossReason && !lossReason) {
      setError(t('adjustErrorLossReason'));
      return;
    }
    // Chặn trước ở FE cho rõ thông báo; BE vẫn chốt cuối bằng 400
    // InsufficientStock khi giảm quá tồn hiện có.
    if (projectedQuantity !== null && projectedQuantity < 0) {
      setError(t('adjustErrorNegative'));
      return;
    }

    createAdjustment(
      {
        product_id: item.productId,
        store_id: storeId,
        type,
        // KHÔNG ÂM — chiều do `type` quyết định ở BE. Gửi số âm là 400.
        quantity: payloadQuantity,
        reason: reason.trim(),
        // Chỉ đính kèm khi BẮT BUỘC — type khác mà có field này là 400, xem
        // ghi chú ở `LOSS_REASONS`.
        ...(requiresLossReason && lossReason ? { loss_reason: lossReason } : {}),
        // Chống trùng — xem ghi chú ở `idempotencyKeyRef`.
        idempotency_key: idempotencyKeyRef.current,
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
        onError: (err: unknown) => {
          // GIỮ NGUYÊN key. Lỗi mạng/timeout không cho biết BE đã ghi hay chưa;
          // sinh key mới rồi gửi lại là tự bỏ hết tác dụng chống trùng và trừ
          // kho lần hai. Key chỉ đổi khi mở modal cho một dòng khác.
          setError(err instanceof Error ? err.message : t('adjustErrorGeneric'));
        },
      }
    );
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[480px] overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t('adjustStockTitle')}</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {item.productName} · {item.sku}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="space-y-4 p-6">
          {/* Loại điều chỉnh */}
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              {t('adjustTypeLabel')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {MANUAL_TYPES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setType(option.value)}
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                    type === option.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {t(option.labelKey)}
                </button>
              ))}
            </div>
          </div>

          {/* Số lượng */}
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              {/* Nhãn đổi theo loại: SET_ABSOLUTE hỏi SỐ ĐẾM ĐƯỢC, không phải
                  mức chênh — gọi nhầm là nhân viên nhập sai ngay lần đầu. */}
              {activeType.direction === 'absolute' ? t('adjustCountedQuantity') : t('quantity')}
              {activeType.direction === 'out' && (
                <span className="ml-1 font-medium normal-case text-red-500">({t('adjustWillDecrease')})</span>
              )}
              {activeType.direction === 'in' && (
                <span className="ml-1 font-medium normal-case text-emerald-600">({t('adjustWillIncrease')})</span>
              )}
            </label>
            <input
              type="number"
              min={0}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder={
                activeType.direction === 'absolute'
                  ? t('adjustCountedQuantityHint')
                  : t('adjustQuantityHint')
              }
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
            />
            <p className="mt-1.5 text-xs text-slate-500">
              {t('currentStock')}: <span className="font-semibold text-slate-700">{item.currentQuantity}</span>
              {projectedQuantity !== null && (
                <>
                  {' → '}
                  <span
                    className={`font-bold ${projectedQuantity < 0 ? 'text-red-600' : 'text-slate-900'}`}
                  >
                    {projectedQuantity}
                  </span>
                </>
              )}
            </p>
          </div>

          {/* Nguyên nhân hao hụt — chỉ hiện khi type thật sự cần, vì BE 400
              nếu type khác mà vẫn gửi kèm field này. */}
          {requiresLossReason && (
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('lossReasonLabel')} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {LOSS_REASONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setLossReason(option.value)}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                      lossReason === option.value
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {t(option.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Lý do — bắt buộc */}
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              {t('reason')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={t('adjustReasonHint')}
              className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button
            onClick={onClose}
            disabled={isPending}
            className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-50"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending}
            className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {isPending ? t('saving') : t('adjustSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
}
